import { useTranslation } from 'react-i18next';
import type { DailyRouteSubject } from '@apis/reports';
import { useTradeLabel } from '@hooks/useTradeLabel';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import styles from './SubjectCard.module.css';

interface ISubjectCardProps {
  subject: DailyRouteSubject;
  /** Archived tag name from the time the report was created. Used to detect
   * tag reassignment — when this differs from the current `subject.name`,
   * the row renders an inline warning banner identifying who the tag is
   * currently assigned to. Mirrors Trusstor's
   * `isTagWasAssignedToDifferentPerson` flag in InvestigationReportPage. */
  archivedName?: string | null;
  /** Pre-formatted long date string (e.g. "May 26th, 2026") interpolated
   * into the warning copy. Only used when the warning renders. */
  reportDateText?: string;
}

/**
 * Single-line banner that identifies the subject of a Daily Route report.
 * Tag: name + tagId chip + " | " + translated trade name.
 * Equipment: name + equipmentId chip + " | " + type.
 * The divider + tail are omitted when the secondary metadata is null.
 *
 * When `archivedName` differs from the current `subject.name` (tag-reassign
 * case), the row renders an inline warning below the main content.
 */
export function SubjectCard({ subject, archivedName, reportDateText }: ISubjectCardProps) {
  const { t } = useTranslation();
  const tradeOf = useTradeLabel();
  const id = subject.kind === 'tag' ? subject.tagId : subject.equipmentId;
  const tail =
    subject.kind === 'tag'
      ? tradeOf(subject.professionTradeId) || null
      : subject.type;
  const showName = subject.name && subject.name !== id;

  // Reassignment warning — only for tag subjects, only when both names are
  // present and they differ. Mirrors Trusstor's flag exactly: a string
  // comparison between the archived `tagName` and the live `subject.name`.
  // `.trim()` defends against whitespace-only archived names, which would
  // otherwise pass the length guard and trigger the warning incorrectly.
  const showReassignWarning =
    subject.kind === 'tag' &&
    typeof archivedName === 'string' &&
    archivedName.trim().length > 0 &&
    archivedName !== subject.name;

  return (
    <div className={styles.card}>
      {showName && <div className={styles.name}>{subject.name}</div>}
      <div className={styles.meta}>
        <CardIcon name="tag" size={16} />
        <span className={styles.idChip}>{id}</span>
        {tail !== null && (
          <>
            <span className={styles.divider} aria-hidden="true">|</span>
            <span className={styles.tail}>{tail}</span>
          </>
        )}
      </div>
      {showReassignWarning && (
        <div className={styles.reassignWarning} role="alert">
          <svg
            className={styles.warningIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6.13 14.63L10.51 5.88c.62-1.23 2.37-1.23 2.98 0l4.38 8.75c.56 1.11-.25 2.41-1.49 2.41H7.62c-1.24 0-2.05-1.31-1.49-2.41Z"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
            />
            <path d="M12 10v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="12" cy="15.2" r="0.6" fill="currentColor" />
          </svg>
          <span>
            {t('reports.dailyRoute.tagReassignWarning', {
              date: reportDateText ?? '',
              // Trusstor interpolates the CURRENT tag name (via
              // `getTagName(props.tag)`), not the archived value. The
              // warning's semantic is "this tag is currently assigned to
              // {currentName}" — the archived-vs-current mismatch is
              // implied by the pill being visible at all.
              name: subject.name,
            })}
          </span>
        </div>
      )}
    </div>
  );
}
